import { apiFetch, apiFetchJson } from '@wundo/client/api.ts';
import type {
  NotificationChannel,
  SendableNotificationChannel,
} from '@wundo/shared/notificationChannel.ts';
import type { NotificationAudience } from '@wundo/shared/notificationHistory.ts';
import type { Block } from 'slack-blocks-to-jsx';
import type { UserRecord } from '@/lib/users.ts';

/**
 * What a moment is about: a meeting the person had, or the person themself.
 * A user moment has no meeting to pick and goes to a whole list at once.
 */
export type NotificationScope = 'meeting' | 'user';

/**
 * Which list a kind sits in: one of Wundo's own moments, or a canned mail. A
 * mail is a notification that only exists on the email channel.
 */
export type NotificationKindGroup = 'moment' | 'email';

/** One of the moments Wundo rings someone about, or one of its mails. */
export interface NotificationKind {
  /* Read from the backend, so a moment or mail added there needs no change here. */
  kind: string;
  label: string;
  scope: NotificationScope;
  /** The channels this moment has a version for, which staff can send it on. */
  channels: SendableNotificationChannel[];
  group: NotificationKindGroup;
}

/** What became of a sent mail, as far as the mail provider has told us. */
export type NotificationOutcome = 'delivered' | 'bounced' | 'complained';

export interface NotificationRecipient {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  organisation_id: string | null;
  organisation_name: string | null;
  /** Where this person is rung — the whole question this console asks. */
  notification_channel: NotificationChannel;
  language: string;
  /** When this moment, or the last copy of this mail, went to this person; null for a meeting moment. */
  sent_at: string | null;
  /** Why sending this user moment would leave this person out; null when they would get it. */
  skip_reason: NotificationSkipReason | null;
  /** For a mail: what became of the last one sent. Null for everything else. */
  last_outcome: NotificationOutcome | null;
  /** When they unsubscribed from unrequested Wundo mail; null while subscribed. */
  email_opt_out_at: string | null;
}

/** A roleplay from an organisation's library, as the "try a roleplay" moment offers it. */
export interface NotificationRoleplay {
  id: string;
  title: string | null;
  type_label: string | null;
}

export interface NotificationMeeting {
  id: string;
  title: string | null;
  starts_at: string;
  ends_at: string;
  over: boolean;
  has_recording: boolean;
  has_summary: boolean;
}

export type NotificationDeliveryReason =
  | 'msteams'
  | 'slack'
  | 'email'
  | 'no_teams_chat'
  | 'no_slack_chat'
  | 'no_webapp'
  | 'teams_only'
  | 'teams_or_email_only'
  | 'no_email_address'
  | 'opted_out'
  | 'channel_none';

export interface NotificationDelivery {
  channel: NotificationChannel;
  reason: NotificationDeliveryReason;
  /** Whether the card carries its button — off without the webapp feature. */
  with_link: boolean;
  /** Whether this moment has already been settled for this person. */
  settled: boolean;
  /** Whether there is nothing to tell this person yet, such as a recap of 1:1s they do not have. */
  nothing_to_say: boolean;
  /** Why a send of this user moment would leave this person out; null for a meeting moment. */
  skip_reason: NotificationSkipReason | null;
}

/** An Adaptive Card's JSON, as the backend hands it to Teams. */
export type AdaptiveCard = Record<string, unknown>;

export interface NotificationPreview {
  delivery: NotificationDelivery;
  email: { subject: string; html: string } | null;
  teams_card: AdaptiveCard | null;
  slack_blocks: Block[];
}

/**
 * A list to pick the recipient out of. Exactly one way in per request — the
 * backend refuses two at once.
 *
 * Picking one person by name does not go through here: the console's own user
 * picker already searches users, and the record behind the id it hands back is
 * read straight from `/users/:id`.
 */
export type NotificationRecipientScope =
  | { kind: 'organisation'; organisationId: string }
  | { kind: 'cohort'; cohortId: string };

export interface NotificationSendParams {
  kind: string;
  userId: string;
  /** Absent for a moment that is about the person rather than a meeting. */
  meetingId?: string;
  /** Addresses of people the ask-Wundo tip must never name; only that moment takes it. */
  neverName?: string[];
  /** The roleplay the "try a roleplay" moment is about; only that moment takes it. */
  roleplayId?: string;
  /** The channel to send it on instead of each person's own; absent means their own. */
  channel?: SendableNotificationChannel;
  /** Send it again to whoever already received it; never for a mail, which always can be. */
  resend?: boolean;
}

/** One press for a whole list: the options of a single send, for everyone ticked. */
export interface NotificationBulkSendParams extends Omit<
  NotificationSendParams,
  'userId' | 'meetingId'
> {
  userIds: string[];
  /** Who the press was aimed at, so the history can name it afterwards. */
  audience?: NotificationAudience;
}

/** Why somebody in a bulk send was not queued, or a mail refused. */
export type NotificationSkipReason =
  | 'not_found'
  | 'settled'
  | 'cannot_see_roleplay'
  | 'already_played'
  | 'magic_link_disabled'
  | 'expired'
  | 'send_failed'
  | NotificationDeliveryReason;

/** What a bulk send did: what was queued, and who was left out of it. */
export interface NotificationBulkSend {
  queued: number;
  skipped: { user_id: string; reason: NotificationSkipReason }[];
}

export async function listNotificationKinds(): Promise<NotificationKind[]> {
  const { data } = await apiFetchJson<{ data: NotificationKind[] }>(
    '/admin/notifications/kinds',
  );
  return data;
}

/** Which list to read, and the moment its rows are checked against. */
export interface NotificationRecipientsQuery {
  scope: NotificationRecipientScope;
  /** Only a moment's list says who has already had it and who a send would leave out. */
  kind?: string;
  roleplayId?: string;
  channel?: SendableNotificationChannel;
  resend?: boolean;
}

/**
 * The list, and — for a moment with a stamp — who has already had it and who
 * a send would leave out.
 */
export async function listNotificationRecipients({
  scope,
  kind,
  roleplayId,
  channel,
  resend = false,
}: NotificationRecipientsQuery): Promise<NotificationRecipient[]> {
  const params = new URLSearchParams(
    scope.kind === 'organisation'
      ? { organisation_id: scope.organisationId }
      : { cohort_id: scope.cohortId },
  );
  if (kind) params.set('kind', kind);
  if (roleplayId) params.set('roleplay_id', roleplayId);
  if (channel) params.set('channel', channel);
  if (resend) params.set('resend', 'true');
  const { data } = await apiFetchJson<{ data: NotificationRecipient[] }>(
    `/admin/notifications/recipients?${params.toString()}`,
  );
  return data;
}

/**
 * One person the user picker handed back, as a recipient.
 *
 * The picker only carries the id, and the record behind it is the same one it
 * resolved its own label from — so it is read where every other screen reads a
 * user, rather than guessed from the label.
 */
export async function getNotificationRecipient(
  userId: string,
): Promise<NotificationRecipient> {
  const user = await apiFetchJson<UserRecord>(`/users/${encodeURIComponent(userId)}`);

  return {
    user_id: user.id,
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email,
    organisation_id: user.organisation_id,
    organisation_name: user.organisation?.name ?? null,
    notification_channel: user.notification_channel,
    language: user.language,
    /* `/users/:id` knows nothing of moments; the preview says what stands. */
    sent_at: null,
    skip_reason: null,
    last_outcome: null,
    email_opt_out_at: user.email_opt_out_at,
  };
}

/** The published roleplays of one organisation's library, in library order. */
export async function listNotificationRoleplays(
  organisationId: string,
): Promise<NotificationRoleplay[]> {
  const params = new URLSearchParams({ organisation_id: organisationId });
  const { data } = await apiFetchJson<{ data: NotificationRoleplay[] }>(
    `/admin/notifications/roleplays?${params.toString()}`,
  );
  return data;
}

export async function listNotificationMeetings(
  userId: string,
): Promise<NotificationMeeting[]> {
  const params = new URLSearchParams({ user_id: userId });
  const { data } = await apiFetchJson<{ data: NotificationMeeting[] }>(
    `/admin/notifications/meetings?${params.toString()}`,
  );
  return data;
}

/* Looking at a notification rings nobody and stamps nothing. */
export function previewNotification({
  kind,
  userId,
  meetingId,
  neverName = [],
  roleplayId,
  channel,
  resend = false,
}: NotificationSendParams): Promise<NotificationPreview> {
  const params = new URLSearchParams({ kind, user_id: userId });
  if (meetingId) params.set('meeting_id', meetingId);
  if (neverName.length > 0) params.set('never_name', neverName.join(','));
  if (roleplayId) params.set('roleplay_id', roleplayId);
  if (channel) params.set('channel', channel);
  if (resend) params.set('resend', 'true');

  return apiFetchJson<NotificationPreview>(
    `/admin/notifications/preview?${params.toString()}`,
  );
}

/* Answers 204 with no body, so nothing is parsed. */
export async function sendNotification({
  kind,
  userId,
  meetingId,
  channel,
  resend = false,
}: NotificationSendParams): Promise<void> {
  await apiFetch('/admin/notifications/actions/send', {
    method: 'POST',
    body: JSON.stringify({
      kind,
      user_id: userId,
      meeting_id: meetingId,
      ...(channel ? { channel } : {}),
      ...(resend ? { resend } : {}),
    }),
  });
}

/**
 * A whole list at once, for a moment that is about the people themselves or a
 * mail. A moment is queued one job per person; a mail has left when this
 * returns. Either way the counts say what that press of the button did.
 */
export function sendNotificationBulk({
  kind,
  userIds,
  neverName = [],
  roleplayId,
  channel,
  audience,
  resend = false,
}: NotificationBulkSendParams): Promise<NotificationBulkSend> {
  return apiFetchJson<NotificationBulkSend>('/admin/notifications/actions/send', {
    method: 'POST',
    body: JSON.stringify({
      kind,
      user_ids: userIds,
      ...(neverName.length > 0 ? { never_name: neverName } : {}),
      ...(roleplayId ? { roleplay_id: roleplayId } : {}),
      ...(channel ? { channel } : {}),
      ...(audience ? { audience } : {}),
      ...(resend ? { resend } : {}),
    }),
  });
}
